import { Logo } from "./Logo.tsx";
import { Outlet, useLocation, useNavigate } from "react-router";
import {
  Avatar,
  Button,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Tab,
  TabList,
  Text,
  Tooltip,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import {
  CalendarLtr24Regular,
  People24Regular,
  Settings24Regular,
  ShieldPersonRegular,
} from "@fluentui/react-icons";
import { useAuth } from "../lib/auth.tsx";

const useStyles = makeStyles({
  root: { display: "flex", flexDirection: "column", minHeight: "100vh", backgroundColor: tokens.colorNeutralBackground2 },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    padding: "0 24px",
    height: "48px",
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
  },
  brand: { fontWeight: tokens.fontWeightSemibold, fontSize: tokens.fontSizeBase400, color: "inherit" },
  body: { display: "flex", flex: 1, minHeight: 0 },
  nav: {
    width: "220px",
    padding: "12px 8px",
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    "@media (max-width: 720px)": { display: "none" },
  },
  main: { flex: 1, padding: "24px", maxWidth: "1100px", width: "100%", boxSizing: "border-box" },
  spacer: { flex: 1 },
});

const NAV = [
  { to: "/", label: "Schedule", icon: <CalendarLtr24Regular /> },
  { to: "/clients", label: "Clients", icon: <People24Regular /> },
  { to: "/settings", label: "Settings", icon: <Settings24Regular /> },
];

export function Shell() {
  const s = useStyles();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const current = NAV.find((n) => (n.to === "/" ? loc.pathname === "/" : loc.pathname.startsWith(n.to)))?.to ?? "/";
  const items = user?.role === "ADMIN" ? [...NAV, { to: "/admin", label: "Admin", icon: <ShieldPersonRegular /> }] : NAV;

  return (
    <div className={s.root}>
      <header className={s.header}>
        <Logo size={26} color="inherit" />
        <div className={s.spacer} />
        <Menu>
          <MenuTrigger disableButtonEnhancement>
            <Tooltip content={user?.email ?? ""} relationship="label">
              <Button appearance="transparent" style={{ color: "inherit" }} icon={<Avatar name={user?.name} image={user?.trainer?.photoUrl ? { src: user.trainer.photoUrl } : undefined} size={28} color="colorful" />}>
                {user?.name}
              </Button>
            </Tooltip>
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              <MenuItem onClick={() => nav("/settings")}>Settings</MenuItem>
              <MenuItem onClick={() => void logout().then(() => nav("/login"))}>Sign out</MenuItem>
            </MenuList>
          </MenuPopover>
        </Menu>
      </header>
      <div className={s.body}>
        <nav className={s.nav}>
          <TabList vertical selectedValue={current} onTabSelect={(_, d) => nav(String(d.value))} size="large">
            {items.map((n) => (
              <Tab key={n.to} value={n.to} icon={n.icon}>
                {n.label}
              </Tab>
            ))}
          </TabList>
        </nav>
        <main className={s.main}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
